// src/app/components/antenna/ai-index-gauge.tsx
//
// Isolated AI Index gauge: owns the count-up tween (useCountUp) so the gauge
// fill + center score animate without re-rendering the parent overview per
// frame. Visual markup is unchanged from the inline version; only the fill is
// driven by the animated value. The AI-chat onClick keeps the REAL score.
import { ClickableMetric } from "../clickable-metric";
import { useCountUp } from "@/app/hooks/use-count-up";

interface AiIndexGaugeProps {
  score: number;
  isAll: boolean;
  velocityColor: string;
  modelDisplay: string;
  onOpenAIChat: (msg: string) => void;
}

export function AiIndexGauge({ score, isAll, velocityColor, modelDisplay, onOpenAIChat }: AiIndexGaugeProps) {
  const animatedScore = useCountUp(score);

  return (
    <div className="relative" style={{ width: "180px", height: "180px" }}>
      {/* Luminous aura behind the gauge, echoes the teal-to-violet arc */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "-14%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(126,95,224,0.16), rgba(0,201,196,0.10) 45%, transparent 68%)",
          pointerEvents: "none",
        }}
      />
      <svg width="180" height="180" viewBox="0 0 180 180" style={{ transform: "rotate(-90deg)" }}>
        {/* Tick Marks Loop */}
        {Array.from({ length: 50 }).map((_, index) => {
          const angle = (index / 50) * 360;
          const isActive = (index / 50) * 100 <= animatedScore;
          const isMajorTick = index % 5 === 0;

          const innerRadius = isMajorTick ? 58 : 62;
          const outerRadius = 75;

          const startX = 90 + innerRadius * Math.cos((angle * Math.PI) / 180);
          const startY = 90 + innerRadius * Math.sin((angle * Math.PI) / 180);
          const endX = 90 + outerRadius * Math.cos((angle * Math.PI) / 180);
          const endY = 90 + outerRadius * Math.sin((angle * Math.PI) / 180);

          const tickColor = isAll
            ? ((index / 50) < 0.5 ? "var(--signal-blue)" : "var(--accent)")
            : velocityColor;

          return (
            <line
              key={index}
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke={isActive ? tickColor : "var(--muted-foreground)"}
              strokeWidth={isActive && isMajorTick ? "2.5" : isActive ? "2" : "1.5"}
              strokeLinecap="round"
              style={{ opacity: isActive ? 1 : 0.2, transition: "all 0.3s ease" }}
            />
          );
        })}

        {/* Progress Circle Arc */}
        <circle
          cx="90"
          cy="90"
          r="68"
          fill="none"
          stroke={isAll ? "url(#gaugeGradient)" : velocityColor}
          strokeWidth="2"
          strokeDasharray={`${(animatedScore / 100) * 427} 427`}
          strokeLinecap="round"
          style={{ opacity: 0.15, filter: "blur(3px)", transition: "all 0.3s ease" }}
        />

        <defs>
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="var(--signal-blue)" />
            <stop offset="100%" stopColor="var(--accent)" />
          </linearGradient>
        </defs>
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <ClickableMetric
          value={Math.round(animatedScore)}
          label={isAll ? "Upmode AI Index" : `${modelDisplay} Score`}
          calculation={isAll ? "Plain average of each tracked model's 0–100 perception judgment." : `${modelDisplay}'s 0–100 perception judgment for your brand.`}
          onClick={() => onOpenAIChat(isAll ? `Explain overall score ${score}` : `Why does ${modelDisplay} score me ${score}?`)}
          style={{ fontSize: "64px", fontWeight: 700, color: isAll ? "var(--accent)" : velocityColor, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
        />
        <div style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", opacity: 0.8, marginTop: "-6px" }}>/100</div>
      </div>
    </div>
  );
}
